import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import Accordion from "../../components/Accordion";
import { Link } from "react-router-dom";

const AboutHabitatBuffalo = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const facts = [
    {
      question: "We know a lot of people.",
      answer: (
        <span>
          Habitat for Humanity International’s Habitat Humanitarians include
          President Jimmy Carter and Rosalynn Carter, Garth Brooks and Trisha
          Yearwood, and Drew and Jonathan Scott. Habitat Humanitarians are a
          group of volunteers who also serve as tireless champions, advocates,
          and spokespeople to raise awareness of the need for decent, affordable
          shelter in the U.S. and around the world.
          <br />
          <br />
          The volunteers who work with Habitat every year aren’t just prominent
          leaders and celebrities, they’re your next-door neighbor, your work
          colleague, or a person like you just looking to make a difference. We
          always have room! <Link to="/get-involved">Get involved</Link>
        </span>
      ),
    },
    {
      question: "We see the benefit of working side by side every day.",
      answer: (
        <span>
          Families who partner with Habitat help build their own homes alongside
          volunteers and pay an affordable mortgage. Homeowners invest their
          time, money, and sweat in building their house. They work beside
          volunteers who work hard to help them construct a home. The money from
          their mortgage goes toward helping another family realize their dream
          of
          <Link to="/how-to-apply/homeownership"> homeownership</Link>.
        </span>
      ),
    },
    {
      question: "We wear many (hard) hats.",
      answer: (
        <span>
          In addition to building new houses, we also partner with families in
          need of a new roof, a wheelchair ramp, or other{" "}
          <Link to="/how-to-apply/home-repairs">critical home repairs</Link>. We
          work to revitalize communities and help those affected by local
          disasters. Globally, our tithe supports Habitat’s work in other
          countries including building shelter, disaster response,
          microfinancing, and more. Beyond building and repairs, Habitat for
          Humanity has an extensive advocacy effort in the U.S. and abroad. This
          effort works to improve less visible aspects of housing, like laws,
          regulations, systems, and policies that affect affordable
          homeownership. Our Cost of Home campaign is a leading effort to
          eliminate barriers to adequate, affordable housing.{" "}
        </span>
      ),
    },

    {
      question: "We’re big into recycling.",
      answer: (
        <span>
          Habitat Buffalo ReStores sell new and gently used furniture, home
          accessories, building materials, and appliances to the public at a
          fraction of the retail price. Donations made to our ReStores help to
          divert millions of pounds from our local landfills. Find a Habitat
          Buffalo <Link to="/restore">ReStore</Link> near you and shop! All
          proceeds from our ReStores go right back into supporting the work of
          Habitat Buffalo.
        </span>
      ),
    },

    {
      question: "We have a following.",
      answer: (
        <span>
          Habitat Buffalo’s social media presence reaches a combined audience of
          thousands of followers.{" "}
          <Link to="/who-we-are/newsroom">Are you one?</Link>
        </span>
      ),
    },
    {
      question: "We can’t do it without you.",
      answer: (
        <span>
          At Habitat Buffalo, we believe that every child needs a place to call
          home. A place where they can grow up healthy, be safe, and learn. Your
          tax-deductible donation – no matter the size – can help a family build
          a more secure future. <Link to="/donate">Give today!</Link>
        </span>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>About Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Learn about Habitat for Humanity Buffalo, our mission, and how we bring people together to build homes, communities, and hope."
        />
      </Helmet>
      <div className="container">
        <h1>About Habitat for Humanity Buffalo</h1>
        <FlexContainer>
          <FlexItem>
            <SectionHeader color="green">Our Mission</SectionHeader>
            <p>
              Seeking to put faith into action, Habitat for Humanity Buffalo
              brings people together to build homes, communities, and hope.
            </p>
          </FlexItem>
          <FlexItem>
            <SectionHeader color="orange">Our Vision</SectionHeader>
            <p>A world where everyone has a decent place to live.</p>
          </FlexItem>
        </FlexContainer>
        <h2 className="section-header primary">What We Do</h2>
        <ContentWrapper>
          <VideoWrapper>
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/pgNvIjjKxoA?si=2lbmBlsdeO5uixWq"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </VideoWrapper>
          <TextWrapper>
            <p>
              The need for housing in our communities provides Habitat for
              Humanity Buffalo with a unique opportunity to change lives in our
              neighborhoods.
            </p>
            <p>
              Buffalo is experiencing a resurgence, and yet many residents still
              face significant challenges in finding affordable, safe housing.
              Habitat for Humanity Buffalo serves families and individuals
              earning 50-120% of the median family income, providing a pathway
              to homeownership.
            </p>
            <p>
              Cost burden and housing insecurity are critical issues for
              low-income Buffalo residents.
            </p>
            <p>
              Working with volunteers, families, individuals, corporations,
              congregations, and donors, we address housing issues throughout
              Western New York. We are dedicated to making homeownership
              possible for low-income, hardworking families and individuals.
            </p>
          </TextWrapper>
        </ContentWrapper>

        <h3 className="section-header orange">
          We Offer a Hand Up, Not a Hand Out
        </h3>
        <p>
          With the support of community partners, donors, and volunteers,
          Habitat Buffalo constructs new homes or renovates existing homes
          alongside Habitat partner homebuyers. Our partner homebuyers make a
          small down payment and monthly mortgage payments, and they invest
          200-500 hours of sweat equity building their future Habitat home and
          the homes of other Habitat homebuyers. Habitat homes are sold to
          partner homebuyers at no profit and are financed with affordable
          loans.
        </p>
        <p>
          Since 1985, we have built, rehabilitated, or repaired nearly 400
          houses in neighborhoods all across Western New York. Shelter is a
          basic human need, and Habitat for Humanity Buffalo strives to achieve
          its goal of eliminating substandard housing by making decent,
          sustainable, and affordable housing a reality for all.
        </p>
        <h3 className="section-header green">
          Our Commitment to the Communities We Serve
        </h3>
        <p>
          Habitat Buffalo believes everyone deserves a safe and decent place to
          live. We bring people together—across backgrounds, beliefs, and
          experiences—to build homes, communities, and hope.
        </p>
        <ul>
          <li>
            We work to ensure fair and open access to housing for all people.
          </li>
          <li>
            We foster a welcoming environment that values individual experiences
            and encourages a sense of belonging.
          </li>
          <li>
            We support strong, connected neighborhoods by expanding access to
            homeownership and community resources.
          </li>
          <li>
            We promote respect, cooperation, and shared purpose among our staff,
            volunteers, and supporters.
          </li>
        </ul>

        <h2 className="section-header primary">
          Fun Facts about Habitat Buffalo
        </h2>
        <p>
          Learn more about Habitat Buffalo's past, our most famous supporters,
          how we work with families, and more.
        </p>
        <FactsList>
          {facts.map((fact, index) => (
            <FactItem key={index}>
              <Accordion
                index={index}
                activeIndex={activeIndex}
                question={fact.question}
                answer={fact.answer}
                toggleAccordion={toggleAccordion}
              />
            </FactItem>
          ))}
        </FactsList>
      </div>
    </>
  );
};

const SectionHeader = styled.h2`
  color: var(--hhb-white);
  background-color: ${(props) => props.color || "var(--primary-color)"};
  padding: 10px;
  margin-bottom: 20px;
  text-align: center;
  border-radius: 5px;

  @media (max-width: 480px) {
    font-size: 1.2em;
    padding: 8px;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FlexItem = styled.div`
  width: 48%;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const VideoWrapper = styled.div`
  flex: 1;
  margin-right: 20px;

  iframe {
    width: 100%;
    height: 315px;
  }

  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 20px;
  }
`;

const TextWrapper = styled.div`
  flex: 2;

  p {
    margin-bottom: 10px;
  }
`;

const FactsList = styled.div`
  margin-top: 20px;
`;

const FactItem = styled.div`
  background: #f9f9f9;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
`;

export default AboutHabitatBuffalo;
